import React, { useMemo } from 'react';
import { Printer, ArrowLeft } from 'lucide-react';
import { Case, RiskLevel } from '../types';

// SA-1: 月次レポート印刷ページ
// 経営層・上司向けの「成果物」を 1 ページの A4 PDF として出力する。
// 外部ライブラリは増やさず window.print() + @media print CSS で実現。
//
// 集計期間: デフォルトで「前月 1 日 00:00:00 JST 〜 当月 1 日 00:00:00 JST」
// （= 確定済みの先月分）。当月運用中のデータが混じると経営層に誤解されるため。
//
// 表示順: 主要指標 → リスク分布 → 4M 分布 → 改善実施記録 TOP5

interface MonthlyReportViewProps {
  cases: Case[];
  organizationName?: string;
  onBack: () => void;
}

function startOfMonthJST(d: Date): Date {
  // JST の月初 00:00 を Date オブジェクトとして返す。比較は UTC で正しく動く。
  const jst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return new Date(Date.UTC(jst.getUTCFullYear(), jst.getUTCMonth(), 1, -9, 0, 0, 0));
}

function previousMonthRange(now: Date): { start: Date; end: Date; label: string } {
  // 当月1日(JST)
  const thisMonthStart = startOfMonthJST(now);
  // 先月1日(JST) = 当月1日から 1 か月戻す
  const prev = new Date(thisMonthStart);
  prev.setUTCMonth(prev.getUTCMonth() - 1);
  const jstStart = new Date(prev.getTime() + 9 * 60 * 60 * 1000);
  const label = `${jstStart.getUTCFullYear()}年${jstStart.getUTCMonth() + 1}月`;
  return { start: prev, end: thisMonthStart, label };
}

export const MonthlyReportView: React.FC<MonthlyReportViewProps> = ({ cases, organizationName, onBack }) => {
  const now = new Date();
  const { start, end, label } = useMemo(() => previousMonthRange(now), [now]);

  // 先月分のケース
  const monthCases = useMemo(() => {
    return cases.filter((c) => {
      const t = new Date(c.createdAt).getTime();
      return t >= start.getTime() && t < end.getTime();
    });
  }, [cases, start, end]);

  // 前々月分（前月比計算用）
  const prevPrev = useMemo(() => {
    const prev = new Date(start);
    prev.setUTCMonth(prev.getUTCMonth() - 1);
    return cases.filter((c) => {
      const t = new Date(c.createdAt).getTime();
      return t >= prev.getTime() && t < start.getTime();
    });
  }, [cases, start]);

  // 主要指標
  const total = monthCases.length;
  const resolved = monthCases.filter((c) => c.status === 'resolved').length;
  const resolveRate = total > 0 ? Math.round((resolved / total) * 100) : 0;
  const highOpen = monthCases.filter((c) => c.riskLevel === RiskLevel.HIGH && c.status !== 'resolved').length;

  const prevTotal = prevPrev.length;
  const prevResolved = prevPrev.filter((c) => c.status === 'resolved').length;
  const prevRate = prevTotal > 0 ? Math.round((prevResolved / prevTotal) * 100) : 0;
  const rateDelta = resolveRate - prevRate;
  const totalDelta = total - prevTotal;

  // 平均滞留日数（resolved した case の created→updated 差分の中央値）
  const pendingDays = (() => {
    const resolvedCases = monthCases.filter((c) => c.status === 'resolved');
    if (resolvedCases.length === 0) return null;
    const days = resolvedCases
      .map((c) => {
        const created = new Date(c.createdAt).getTime();
        // updatedAt が無いケースが既存型にあるため createdAt をフォールバック
        const updated = new Date((c as Case & { updatedAt?: string }).updatedAt ?? c.createdAt).getTime();
        return Math.max(0, (updated - created) / (1000 * 60 * 60 * 24));
      })
      .sort((a, b) => a - b);
    const mid = Math.floor(days.length / 2);
    return days.length % 2 ? days[mid] : (days[mid - 1] + days[mid]) / 2;
  })();

  // リスク分布
  const riskCounts = {
    High: monthCases.filter((c) => c.riskLevel === RiskLevel.HIGH).length,
    Medium: monthCases.filter((c) => c.riskLevel === RiskLevel.MEDIUM).length,
    Low: monthCases.filter((c) => c.riskLevel === RiskLevel.LOW).length,
  };

  // 4M 分布（category1）
  const categoryGroups = monthCases.reduce<Record<string, number>>((acc, c) => {
    acc[c.category1 ?? '未分類'] = (acc[c.category1 ?? '未分類'] || 0) + 1;
    return acc;
  }, {});

  // 改善実施記録 TOP5（status=resolved の最新5件）
  const resolvedTop5 = monthCases
    .filter((c) => c.status === 'resolved')
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  return (
    <div className="bg-slate-100 min-h-screen py-6">
      <style>{`
        @media print {
          body { background: white !important; }
          .no-print { display: none !important; }
          .print-page {
            box-shadow: none !important;
            border: none !important;
            margin: 0 !important;
            padding: 12mm !important;
            max-width: none !important;
          }
          @page {
            size: A4;
            margin: 0;
          }
        }
        .print-page { font-feature-settings: "palt"; }
      `}</style>

      {/* 画面用ヘッダ（印刷時は非表示） */}
      <div className="no-print max-w-3xl mx-auto px-4 mb-4 flex items-center justify-between gap-3">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-1.5 text-sm text-slate-700 hover:text-slate-900 bg-white border border-slate-200 rounded-lg px-3 py-2 shadow-sm"
        >
          <ArrowLeft size={16} /> ダッシュボードに戻る
        </button>
        <button
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 text-sm font-medium text-white bg-industrial-900 hover:bg-slate-800 rounded-lg px-4 py-2 shadow"
        >
          <Printer size={16} /> このページをPDFとして保存
        </button>
      </div>

      <div className="no-print max-w-3xl mx-auto px-4 mb-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-900">
          <p className="font-bold mb-1">使い方</p>
          <p>「PDFとして保存」を押すと、ブラウザの印刷ダイアログが開きます。送信先で「PDFとして保存」を選んでください（A4 1枚に収まります）。</p>
        </div>
      </div>

      {/* 印刷対象ページ */}
      <div className="print-page bg-white max-w-3xl mx-auto px-8 py-10 shadow border border-slate-200 rounded">
        {/* ヘッダ */}
        <div className="flex items-end justify-between border-b-2 border-industrial-900 pb-3 mb-6">
          <div>
            <p className="text-xs text-slate-500 font-medium">月次安全衛生レポート</p>
            <h1 className="text-2xl font-extrabold text-industrial-900 mt-0.5">{label}</h1>
            {organizationName && (
              <p className="text-sm text-slate-700 mt-1">{organizationName}</p>
            )}
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-400">発行</p>
            <p className="text-sm font-bold text-slate-700">安全ポスト＋</p>
            <p className="text-[10px] text-slate-400">anzenpost.com</p>
          </div>
        </div>

        {/* 主要指標 */}
        <section className="mb-6">
          <h2 className="text-sm font-bold text-industrial-900 border-l-4 border-orange-500 pl-2 mb-3">主要指標</h2>
          <div className="grid grid-cols-4 gap-3 text-center">
            <div className="border border-slate-200 rounded p-2">
              <p className="text-[10px] text-slate-500">投稿総数</p>
              <p className="text-xl font-extrabold text-slate-900 leading-tight">{total}</p>
              <p className="text-[10px] text-slate-500">前月比 {totalDelta >= 0 ? '+' : ''}{totalDelta}</p>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <p className="text-[10px] text-slate-500">解決率</p>
              <p className="text-xl font-extrabold text-emerald-700 leading-tight">{resolveRate}%</p>
              <p className="text-[10px] text-slate-500">前月比 {rateDelta >= 0 ? '+' : ''}{rateDelta}pt</p>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <p className="text-[10px] text-slate-500">High 未対応</p>
              <p className={`text-xl font-extrabold leading-tight ${highOpen > 0 ? 'text-red-700' : 'text-slate-400'}`}>{highOpen}</p>
              <p className="text-[10px] text-slate-500">件</p>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <p className="text-[10px] text-slate-500">平均滞留日数</p>
              <p className="text-xl font-extrabold text-amber-700 leading-tight">
                {pendingDays === null ? '—' : pendingDays.toFixed(1)}
              </p>
              <p className="text-[10px] text-slate-500">日 (対応済みの中央値)</p>
            </div>
          </div>
        </section>

        {/* リスク分布 */}
        <section className="mb-6">
          <h2 className="text-sm font-bold text-industrial-900 border-l-4 border-orange-500 pl-2 mb-3">リスクレベル分布</h2>
          <table className="w-full text-sm border border-slate-200 rounded overflow-hidden">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-3 py-1.5 border-b border-slate-200 font-semibold">リスク</th>
                <th className="text-right px-3 py-1.5 border-b border-slate-200 font-semibold">件数</th>
                <th className="text-right px-3 py-1.5 border-b border-slate-200 font-semibold">割合</th>
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'High（高）', count: riskCounts.High, color: 'text-red-700' },
                { label: 'Medium（中）', count: riskCounts.Medium, color: 'text-yellow-700' },
                { label: 'Low（低）', count: riskCounts.Low, color: 'text-emerald-700' },
              ].map((r) => (
                <tr key={r.label} className="border-b last:border-b-0 border-slate-100">
                  <td className={`px-3 py-1.5 font-medium ${r.color}`}>{r.label}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.count}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                    {total > 0 ? Math.round((r.count / total) * 100) : 0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {/* 4M 分布 */}
        <section className="mb-6">
          <h2 className="text-sm font-bold text-industrial-900 border-l-4 border-orange-500 pl-2 mb-3">4M カテゴリ分布</h2>
          {Object.keys(categoryGroups).length === 0 ? (
            <p className="text-xs text-slate-500">先月の投稿はありませんでした。</p>
          ) : (
            <div className="grid grid-cols-4 gap-2 text-sm">
              {['Man (人)', 'Machine (機械)', 'Material (材料)', 'Method (方法)'].map((cat) => (
                <div key={cat} className="border border-slate-200 rounded p-2">
                  <p className="text-[10px] text-slate-500">{cat}</p>
                  <p className="text-lg font-bold text-slate-800 leading-tight">{categoryGroups[cat] || 0}<span className="text-xs font-medium text-slate-500"> 件</span></p>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* 改善実施記録 TOP5 */}
        <section className="mb-6">
          <h2 className="text-sm font-bold text-industrial-900 border-l-4 border-orange-500 pl-2 mb-3">改善実施記録 TOP5（対応済み）</h2>
          {resolvedTop5.length === 0 ? (
            <p className="text-xs text-slate-500">先月に解決として記録された案件はありませんでした。</p>
          ) : (
            <ol className="space-y-2 text-sm">
              {resolvedTop5.map((c, i) => (
                <li key={c.id} className="border border-slate-200 rounded p-2">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-bold text-slate-400 mt-0.5">#{i + 1}</span>
                    <div className="flex-1">
                      <p className="font-bold text-slate-800 text-sm leading-tight">{c.title}</p>
                      <p className="text-[11px] text-slate-500 mt-1">
                        {c.category1} / {c.category2} · リスク: {c.riskLevel} · {new Date(c.createdAt).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                      </p>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </section>

        {/* フッタ */}
        <div className="mt-8 pt-3 border-t border-slate-200 flex justify-between text-[10px] text-slate-400">
          <span>本レポートは安全ポスト＋ の管理画面から自動生成されました</span>
          <span>{new Date().toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })} 発行</span>
        </div>
      </div>
    </div>
  );
};
